/*
 * PAGE GROUPS (2026-10-21; docs/PAGE_GROUPS_DESIGN.md, phase 2): the dashboard's copy of the site's groups and suggestions, the state of the editor and of "pick pages to group", and the actions that change
 * them. A plain module with events, like notesModel.ts and goalsModel.ts: every screen reads from here and listens, so they cannot disagree.
 *
 * A change goes to the server FIRST and the screens are updated from its answer, so a group is never shown that the server refused. When the server accepts one it also changes the page list (a group takes
 * over the pages it covers), so the new list is handed to the dashboard the same way the server's own `pages-changed` event does (and every OTHER open dashboard gets that event from the server).
 */
import { eventEmitter } from "../eventEmitters/EventEmitters";
import storage from "../util/storage";
import { Candidate, fetchCandidates, fetchGroups, Members, PageGroupWire, requestCreate, requestDelete, requestUpdate, SaveResult } from "./pageGroupsApi";
import type { Dismissals } from "./pageGroupsView";

let groups: PageGroupWire[] = [];
let candidates: Candidate[] = [];
let loadedFor: string | null = null;

const currentSiteId = (): string => String(storage.get("siteId") || "");

/** the site's groups. Empty until they have been loaded for THIS site (after switching sites it is never the old site's). */
export const groupsList = (): PageGroupWire[] => (loadedFor && loadedFor === currentSiteId() ? groups : []);
export const candidatesList = (): Candidate[] => (loadedFor && loadedFor === currentSiteId() ? candidates : []);

const changed = () => eventEmitter.emit("pageGroupsUpdated");

export const loadGroups = async (): Promise<boolean> => {
  const site = currentSiteId();
  const [g, c] = await Promise.all([fetchGroups(), fetchCandidates()]);
  if (site !== currentSiteId()) return false;           // the person switched sites while this was on its way
  if (!g) return false;
  groups = g;
  candidates = c ?? [];
  loadedFor = site;
  changed();

  return true;
};

/** forget everything (a test, or a site switch) */
export const resetPageGroups = () => { groups = []; candidates = []; loadedFor = null; editor = null; picking = false; ticked = new Set(); changed(); };

const handOverPages = (pages: any[] | undefined) => {
  if (Array.isArray(pages)) eventEmitter.emit("pages-changed", pages);
};

const afterSave = async <T extends { ok: boolean }>(result: T & { pages?: any[] }): Promise<T> => {
  if (result.ok) {
    handOverPages(result.pages);
    await loadGroups();
  }

  return result;
};

export const createGroup = async (members: Members): Promise<SaveResult> => afterSave(await requestCreate(members));
export const updateGroup = async (groupId: string, members: Members): Promise<SaveResult> => afterSave(await requestUpdate(groupId, members));
export const deleteGroup = async (groupId: string) => afterSave(await requestDelete(groupId));

// ── suggestions the owner put off ──
const dismissKey = (): string => `pageGroupDismissed:${currentSiteId()}`;

export const readDismissals = (): Dismissals => {
  try {
    const raw = storage.get(dismissKey());
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;

    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

export const dismissSuggestion = (pattern: string, nowMs: number = Date.now()) => {
  try {
    storage.set(dismissKey(), JSON.stringify({ ...readDismissals(), [pattern]: nowMs }));
  } catch {
    // storage may be blocked: the suggestion simply comes back next time
  }
  changed();
};

// ── the editor ──
export interface EditorSpec {
  mode: "create" | "edit";
  /** a group made from a whole branch of the outline: the form asks only for a name, the rule is already decided */
  quick?: boolean;
  group?: PageGroupWire;
  /** what the form starts with (a suggestion, ticked pages, or a page's own name) */
  initial?: { name?: string; patterns?: string[]; pages?: string[] };
}

let editor: EditorSpec | null = null;
export const editorSpec = (): EditorSpec | null => editor;
export const openEditor = (spec: EditorSpec) => { editor = spec; picking = false; ticked = new Set(); eventEmitter.emit("pageGroupEditor"); changed(); };
export const closeEditor = () => { editor = null; eventEmitter.emit("pageGroupEditor"); };

// ── which group rows are opened to show the pages inside ──
const expanded = new Set<string>();
export const isExpanded = (key: string): boolean => expanded.has(key);
export const toggleExpanded = (key: string) => { if (expanded.has(key)) expanded.delete(key); else expanded.add(key); changed(); };

// ── "pick pages to group" ──
let picking = false;
let ticked = new Set<string>();
export const isPicking = (): boolean => picking;
export const tickedPages = (): string[] => Array.from(ticked);
export const startPicking = () => { picking = true; ticked = new Set(); eventEmitter.emit("pageGroupPicking"); };
export const stopPicking = () => { picking = false; ticked = new Set(); eventEmitter.emit("pageGroupPicking"); };
export const toggleTick = (name: string) => {
  const next = new Set(ticked);
  if (next.has(name)) next.delete(name);
  else next.add(name);
  ticked = next;
  eventEmitter.emit("pageGroupPicking");
};
